/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';

import type {FileUploadHandler} from '../../common/directives/file-upload/file-upload.util';
import {ContextServiceKeywordUploadApiService} from '../context-service-configuration/context-service-keyword-upload-api.service';
import {FieldMappingApiService} from '../../mapping/field-mapping-api.service';
import {ReferenceDataApiService} from './reference-data-api.service';
import {CreativeAssetsModule} from '../../creative/creative-assets.module';

@Component({
  standalone: true,
  selector: 'adn-reference-data-upload',
  imports: [CreativeAssetsModule],
  template: `
    <adn-file-upload
      [name]="name"
      [accept]="accept"
      [fileUploadLimit]="1"
      [fileLimitExceededSingular]="true"
      dropTranslationKey="button.upload.asset.drop"
      [warningOptions]="warningOptions"
      successWarningsPath="data.validationWarnings"
      [uploadFile]="uploadReferenceDataFile"
      (onUpdate)="onUpdate.emit()">
    </adn-file-upload>
  `
})
export class ReferenceDataUploadComponent implements OnInit, OnChanges {
  @Input() name = 'upload';
  @Input() uploadType= 'categories';
  @Input() resourceId: any = '';
  @Output() onUpdate = new EventEmitter<void>();

  accept = '.txt';
  readonly warningOptions = {mode: 'reference-data' as const};

  constructor(
    private readonly referenceDataApi: ReferenceDataApiService,
    private readonly fieldMappingApi: FieldMappingApiService,
    private readonly contextServiceKeywordUploadApi: ContextServiceKeywordUploadApiService
  ) {
  }

  ngOnInit(): void {
    this.syncUploadConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.uploadType) {
      this.syncUploadConfig();
    }
  }

  uploadReferenceDataFile: FileUploadHandler = (file, onProgress) => {
    const resource = this.resolveUploadResource();
    const uploadType = this.resolveUploadType();
    const uploadPromise = this.uploadType === 'trackedKeywords'
      ? resource.upload({file: file}, uploadType, this.resourceId || '', onProgress)
      : resource.upload({file: file}, uploadType, onProgress);
    return uploadPromise.then(
      (fileResponse: any) => fileResponse,
      (error: any) => Promise.reject(error)
    );
  };

  private resolveUploadType(): string {
    if (this.uploadType === 'fieldmappings') {
      return 'fieldmappings';
    }
    if (this.uploadType === 'trackedKeywords') {
      return 'trackedKeywords';
    }
    if (this.uploadType === 'keyvalues') {
      return 'keyvalues';
    }
    return 'categories';
  }

  private syncUploadConfig(): void {
    this.accept = this.uploadType === 'fieldmappings' || this.uploadType === 'trackedKeywords' ? '.csv' : '.txt';
  }

  private resolveUploadResource(): any {
    if (this.uploadType === 'fieldmappings') {
      return this.fieldMappingApi;
    }
    if (this.uploadType === 'trackedKeywords') {
      return this.contextServiceKeywordUploadApi;
    }
    return this.referenceDataApi;
  }
}
